import { FormEvent, useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import { Textarea } from '@/shared/components/ui/textarea';
import { InlineFileUpload } from '@/shared/components/file-upload/InlineFileUpload';
import { FpStructuredProfileFields } from '@/shared/components/fp-profile/FpStructuredProfileFields';
import type {
  ClientSegment,
  InvestableAssetsRange,
  ServiceOffering,
} from '@/shared/constants/fpProfileOptions';
import {
  useUpdateCurrentFinancialAdvisorProfile,
} from '@/features/provider-directory/hooks/useFinancialAdvisorProfile';
import type {
  FinancialAdvisorProfile,
  UpdateFinancialAdvisorProfilePayload,
} from '@/features/provider-directory/types/financial-advisor-profile.types';
import { AdvisorProfileStatusBanner } from './AdvisorProfileStatusBanner';
import { AdvisorProfileUpdateRequestModal } from './AdvisorProfileUpdateRequestModal';
import { useToast } from '@/shared/components/Toast';

const BIOGRAPHY_MAX_LENGTH = 1000;
const STATE_CODE_PATTERN = /^[A-Z]{2}$/i;

type AdvisorInformationTabProps = {
  profile: FinancialAdvisorProfile;
};

type AdvisorProfileFormState = {
  companyName: string;
  // Feature 074 structured fields.
  predominantClientSegments: ClientSegment[];
  investableAssets: InvestableAssetsRange | '';
  serviceOfferingsList: ServiceOffering[];
  idealClientProfile: string;
  differentiatingFactors: string;
  advisorBiography: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  profilePhoto: string;
  profilePrivate: boolean;
};

const toFormState = (profile: FinancialAdvisorProfile): AdvisorProfileFormState => ({
  companyName: profile.companyName ?? '',
  predominantClientSegments: profile.predominantClientSegments ?? [],
  investableAssets: profile.investableAssets ?? '',
  serviceOfferingsList: profile.serviceOfferingsList ?? [],
  idealClientProfile: profile.idealClientProfile ?? '',
  differentiatingFactors: profile.differentiatingFactors ?? '',
  advisorBiography: profile.advisorBiography ?? '',
  addressLine1: profile.addressLine1 ?? '',
  addressLine2: profile.addressLine2 ?? '',
  addressCity: profile.addressCity ?? '',
  addressState: profile.addressState ?? '',
  addressZip: profile.addressZip ?? '',
  profilePhoto: profile.user.profilePhoto ?? '',
  profilePrivate: profile.visibility?.profilePrivate ?? false,
});

const trimmedOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

// Company Overview is intentionally excluded: it is admin-managed and the server rejects
// FA-supplied values with 403. FAs can request changes via the "Request Changes" modal.
// `serviceOfferingsTouched` gates whether we send serviceOfferingsList — sending it clears
// the advisor's legacy free-text offerings, so we only send it when they actually edit it.
const toPayload = (
  formState: AdvisorProfileFormState,
  serviceOfferingsTouched: boolean,
): UpdateFinancialAdvisorProfilePayload => ({
  companyName: formState.companyName,
  predominantClientSegments: formState.predominantClientSegments,
  // Always send investableAssets (value or null) so clearing the dropdown clears the field.
  investableAssets: formState.investableAssets || null,
  ...(serviceOfferingsTouched ? { serviceOfferingsList: formState.serviceOfferingsList } : {}),
  idealClientProfile: formState.idealClientProfile,
  differentiatingFactors: formState.differentiatingFactors,
  advisorBiography: trimmedOrNull(formState.advisorBiography),
  addressLine1: trimmedOrNull(formState.addressLine1),
  addressLine2: trimmedOrNull(formState.addressLine2),
  addressCity: trimmedOrNull(formState.addressCity),
  addressState: formState.addressState.trim()
    ? formState.addressState.trim().toUpperCase()
    : null,
  addressZip: trimmedOrNull(formState.addressZip),
  profilePhoto: formState.profilePhoto || null,
  profilePrivate: formState.profilePrivate,
});

export const AdvisorInformationTab = ({ profile }: AdvisorInformationTabProps) => {
  const [formState, setFormState] = useState<AdvisorProfileFormState>(() => toFormState(profile));
  const [saveError, setSaveError] = useState('');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  // Feature 074: track whether the advisor edited Service Offerings this session so we only
  // send (and thus clear legacy free text) when they actually re-pick.
  const [serviceOfferingsTouched, setServiceOfferingsTouched] = useState(false);
  const updateProfile = useUpdateCurrentFinancialAdvisorProfile();
  const { showToast } = useToast();
  const isAdminHidden = profile.visibility?.hideFromServiceProviders === true;
  const hasLegacyServiceOfferings = !!profile.serviceOfferings && formState.serviceOfferingsList.length === 0;
  const isDirty =
    serviceOfferingsTouched ||
    JSON.stringify(formState) !== JSON.stringify(toFormState(profile));
  const bioOverLimit = formState.advisorBiography.length > BIOGRAPHY_MAX_LENGTH;
  const trimmedState = formState.addressState.trim();
  const stateCodeInvalid = trimmedState.length > 0 && !STATE_CODE_PATTERN.test(trimmedState);
  // On re-pick from legacy free text, at least one controlled offering must be selected before saving.
  const serviceOfferingsInvalid =
    serviceOfferingsTouched && hasLegacyServiceOfferings && formState.serviceOfferingsList.length === 0;
  const hasBlockingValidationError = bioOverLimit || stateCodeInvalid || serviceOfferingsInvalid;

  useEffect(() => {
    setFormState(toFormState(profile));
    setServiceOfferingsTouched(false);
  }, [profile]);

  const updateField = (field: keyof AdvisorProfileFormState, value: string | boolean) => {
    setFormState((current) => ({ ...current, [field]: value }));
    setSaveError('');
  };

  const handleServiceOfferingsChange = (value: ServiceOffering[]) => {
    setServiceOfferingsTouched(true);
    setSaveError('');
    setFormState((current) => ({ ...current, serviceOfferingsList: value }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaveError('');

    if (hasBlockingValidationError) return;

    try {
      await updateProfile.mutateAsync(toPayload(formState, serviceOfferingsTouched));
      showToast('Profile successfully updated!', 'success');
    } catch (error) {
      console.error('Error saving advisor profile:', error);
      setSaveError('Unable to save profile changes. Please try again.');
    }
  };

  return (
    <div className="space-y-4">
      <AdvisorProfileStatusBanner profile={profile} />

      <form onSubmit={handleSubmit} className="space-y-4">
        <Card className="p-6 space-y-4">
          <h2 className="text-xl text-gray-900">Profile Information</h2>

          <div>
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={formState.companyName}
              onChange={(event) => updateField('companyName', event.target.value)}
              disabled={updateProfile.isPending}
            />
          </div>

          <div>
            <InlineFileUpload
              label="Profile Photo"
              value={formState.profilePhoto}
              onChange={(value) => updateField('profilePhoto', value)}
              accept="image/*"
              maxSizeMB={5}
              hint="Square avatar image recommended (5MB max)"
              previewClassName="h-32 w-32 rounded-full"
              disabled={updateProfile.isPending}
            />
          </div>

          <div>
            <Label htmlFor="advisorBiography">Advisor Biography</Label>
            <p className="text-sm text-gray-600 mb-1" id="advisorBiography-description">
              Shown above your Company Overview on your public profile.
            </p>
            <Textarea
              id="advisorBiography"
              value={formState.advisorBiography}
              onChange={(event) => updateField('advisorBiography', event.target.value)}
              disabled={updateProfile.isPending}
              aria-describedby="advisorBiography-description advisorBiography-counter"
              aria-invalid={bioOverLimit}
            />
            <p
              id="advisorBiography-counter"
              className={
                bioOverLimit
                  ? 'text-xs text-red-600 mt-1'
                  : 'text-xs text-gray-500 mt-1'
              }
              aria-live="polite"
            >
              {formState.advisorBiography.length} / {BIOGRAPHY_MAX_LENGTH}
            </p>
            {bioOverLimit && (
              <p className="text-sm text-red-600 mt-1">
                Biography must be {BIOGRAPHY_MAX_LENGTH} characters or fewer
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="companyOverview">Company Overview</Label>
            <p className="text-sm text-gray-600 mb-1" id="companyOverview-description">
              Admin-managed. Use <strong>Request Changes</strong> below to suggest edits.
            </p>
            <div
              id="companyOverview"
              role="region"
              aria-label="Company Overview (read-only)"
              aria-describedby="companyOverview-description"
              data-testid="company-overview-readonly"
              className="min-h-[80px] rounded-md border border-slate-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 whitespace-pre-wrap"
            >
              {profile.companyOverview?.trim()
                ? profile.companyOverview
                : <span className="italic text-gray-500">No company overview set yet.</span>}
            </div>
          </div>

          <FpStructuredProfileFields
            clientSegments={formState.predominantClientSegments}
            investableAssets={formState.investableAssets}
            serviceOfferings={formState.serviceOfferingsList}
            hasLegacyServiceOfferings={hasLegacyServiceOfferings}
            serviceOfferingsError={
              serviceOfferingsInvalid ? 'Select at least one service offering' : undefined
            }
            disabled={updateProfile.isPending}
            onClientSegmentsChange={(value) =>
              setFormState((current) => ({ ...current, predominantClientSegments: value }))
            }
            onInvestableAssetsChange={(value) =>
              setFormState((current) => ({ ...current, investableAssets: value }))
            }
            onServiceOfferingsChange={handleServiceOfferingsChange}
          />

          <div>
            <Label htmlFor="idealClientProfile">Ideal Client Profile</Label>
            <Textarea
              id="idealClientProfile"
              value={formState.idealClientProfile}
              onChange={(event) => updateField('idealClientProfile', event.target.value)}
              disabled={updateProfile.isPending}
            />
          </div>

          <div>
            <Label htmlFor="differentiatingFactors">Differentiating Factors</Label>
            <Textarea
              id="differentiatingFactors"
              value={formState.differentiatingFactors}
              onChange={(event) => updateField('differentiatingFactors', event.target.value)}
              disabled={updateProfile.isPending}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg bg-gray-50 p-4">
            <div>
              <Label htmlFor="profilePrivate">Make Profile Private</Label>
              <p className="text-sm text-gray-600">
                Hide your profile from Service Provider searches and direct access.
              </p>
            </div>
            <Switch
              id="profilePrivate"
              aria-label="Make Profile Private"
              checked={formState.profilePrivate}
              onCheckedChange={(checked) => updateField('profilePrivate', checked)}
              disabled={updateProfile.isPending || isAdminHidden}
            />
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h2 className="text-xl text-gray-900">Address</h2>
          <p className="text-sm text-gray-600">
            Used to match your profile against Service Provider searches by state.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="addressLine1">Street Address</Label>
              <Input
                id="addressLine1"
                value={formState.addressLine1}
                onChange={(event) => updateField('addressLine1', event.target.value)}
                disabled={updateProfile.isPending}
              />
            </div>
            <div>
              <Label htmlFor="addressLine2">Apt / Suite (optional)</Label>
              <Input
                id="addressLine2"
                value={formState.addressLine2}
                onChange={(event) => updateField('addressLine2', event.target.value)}
                disabled={updateProfile.isPending}
              />
            </div>
            <div>
              <Label htmlFor="addressCity">City</Label>
              <Input
                id="addressCity"
                value={formState.addressCity}
                onChange={(event) => updateField('addressCity', event.target.value)}
                disabled={updateProfile.isPending}
              />
            </div>
            <div>
              <Label htmlFor="addressState">State (2-letter)</Label>
              <Input
                id="addressState"
                value={formState.addressState}
                maxLength={2}
                onChange={(event) =>
                  updateField('addressState', event.target.value.toUpperCase())
                }
                disabled={updateProfile.isPending}
                aria-invalid={stateCodeInvalid}
                aria-errormessage={stateCodeInvalid ? 'addressState-error' : undefined}
              />
              {stateCodeInvalid && (
                <p id="addressState-error" className="text-sm text-red-600 mt-1">
                  State must be a two-letter US state code
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="addressZip">ZIP</Label>
              <Input
                id="addressZip"
                value={formState.addressZip}
                onChange={(event) => updateField('addressZip', event.target.value)}
                disabled={updateProfile.isPending}
              />
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl text-gray-900">Admin-only fields</h2>
            <Button type="button" variant="outline" onClick={() => setIsRequestModalOpen(true)}>
              Request Changes
            </Button>
          </div>
          <dl className="grid gap-3 text-sm md:grid-cols-2">
            <div>
              <dt className="font-medium text-gray-600">Name</dt>
              <dd>{[profile.user.firstName, profile.user.lastName].filter(Boolean).join(' ')}</dd>
            </div>
            {profile.user.email && (
              <div>
                <dt className="font-medium text-gray-600">Email</dt>
                <dd>{profile.user.email}</dd>
              </div>
            )}
            <div>
              <dt className="font-medium text-gray-600">Account Status</dt>
              <dd>{profile.user.accountStatus}</dd>
            </div>
            <div>
              <dt className="font-medium text-gray-600">Admin Hide</dt>
              <dd>{isAdminHidden ? 'Hidden by administrator' : 'Not hidden by administrator'}</dd>
            </div>
          </dl>
        </Card>

        {saveError && (
          <p className="text-sm text-red-600" role="alert">
            {saveError}
          </p>
        )}

        <Button
          type="submit"
          disabled={!isDirty || updateProfile.isPending || hasBlockingValidationError}
        >
          {updateProfile.isPending ? 'Saving...' : 'Save Profile'}
        </Button>
      </form>

      <AdvisorProfileUpdateRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => setIsRequestModalOpen(false)}
        profile={profile}
      />
    </div>
  );
};
